/**
 * Scoped Redo presentation for the activated native cart drawer.
 * Relabels exact "Checkout+" prefixes and injects shadow styles only.
 * Does not replace buttons, intercept clicks, calculate prices, or mutate the cart.
 */
(function (root) {
  if (root.NativeCartRedo) return;

  var SOURCE_LABEL = 'Checkout+';
  var SECURE_LABEL = 'Secure checkout+';
  var HOST_TAG = 'REDO-SHOPIFY-TOGGLE';
  var HOST_SELECTOR = 'redo-shopify-toggle';
  var NATIVE_DRAWER = '#CartDrawer.is-native-cart-custom';
  var STYLE_ID = 'native-cart-redo-style';
  var EVENTS = [
    'redo:checkout-buttons-rendered',
    'redo:info-card-rendered',
    'redo:toggle-rendered'
  ];
  var RELABEL_SELECTORS = [
    '[data-target="headline-text-container"]',
    '[data-target="toggle-label"]',
    '[data-target="coverage-button"]'
  ];
  var OBSERVE_ROOT = {
    childList: true,
    subtree: true,
    characterData: true,
    attributes: true,
    attributeFilter: ['aria-label']
  };
  var LOCK_MASK = "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect x='5' y='10' width='14' height='11' rx='2'/%3E%3Cpath d='M8 10V7a4 4 0 0 1 8 0v3'/%3E%3Cpath d='M12 14v3'/%3E%3C/svg%3E\")";
  var STYLE_TEXT = [
    '[data-target="info-card-container"]{background:#f7f5f1;border:1px solid #e7e2da;border-radius:8px;padding:12px;box-sizing:border-box;}',
    '[data-target="info-card-container"] [data-target="headline-text-container"],[data-target="toggle-label"]{font-size:13px;line-height:1.3;}',
    '[data-target="subtitle-text-container"],[data-target="toggle-subtext"]{font-size:12px;}',
    '[data-target="coverage-button"]{background:#205d64;color:#fff;border:1px solid #205d64;border-radius:6px;min-height:50px;width:100%;font-size:15px;display:flex;align-items:center;justify-content:center;gap:8px;padding:12px;}',
    '[data-target="coverage-button"] [data-target="headline-text-container"]{color:#fff;font-size:15px;line-height:1.3;width:auto;flex:0 1 auto;display:flex;align-items:center;justify-content:center;gap:8px;}',
    '[data-target="coverage-button"] [data-target="headline-text-container"]>*{color:inherit;font-size:inherit;width:auto;}',
    '[data-target="subtitle-text-container"]:has(>:only-child:empty){display:none;}',
    '[data-target="coverage-button"] [data-target="headline-text-container"]::before{content:"";width:18px;height:18px;flex:0 0 18px;background-color:currentColor;-webkit-mask-image:' + LOCK_MASK + ';-webkit-mask-repeat:no-repeat;-webkit-mask-position:center;-webkit-mask-size:18px 18px;mask-image:' + LOCK_MASK + ';mask-repeat:no-repeat;mask-position:center;mask-size:18px 18px;}',
    '[data-target="non-coverage-button"]{font-size:12px;line-height:1.4;min-height:44px;white-space:normal;overflow-wrap:anywhere;word-wrap:break-word;}'
  ].map(function (rule) {
    // Outrank Redo's later-injected merchant styles without changing its config.
    return rule.replace(/^([^{}]+)\{/, function (match, selectors) {
      return selectors.split(',').map(function (selector) {
        return ':host(redo-shopify-toggle) ' + selector;
      }).join(',') + '{';
    });
  }).join('').replace(/([^{};]+):([^{};]+);/g, '$1:$2 !important;');

  var hostObservers = typeof WeakMap === 'function' ? new WeakMap() : null;
  var liveHosts = [];
  var lightObservers = typeof WeakMap === 'function' ? new WeakMap() : null;
  var boundDocs = typeof WeakSet === 'function' ? new WeakSet() : null;

  function viewOf(node) {
    var doc = node && node.ownerDocument ? node.ownerDocument : node;
    if (doc && doc.defaultView) return doc.defaultView;
    if (typeof window !== 'undefined') return window;
    return root;
  }

  function getNativeDrawer(doc) {
    if (!doc || typeof doc.querySelector !== 'function') return null;
    return doc.querySelector(NATIVE_DRAWER);
  }

  function isInNativeDrawer(node) {
    if (!node || typeof node.closest !== 'function') return false;
    return !!node.closest(NATIVE_DRAWER);
  }

  function getToggleRoot(host) {
    if (!host) return null;
    var shadow = null;
    try {
      shadow = host.shadowRoot || host.root;
    } catch (err) {
      return null;
    }
    if (!shadow || typeof shadow.querySelector !== 'function') return null;
    return shadow;
  }

  function replaceCheckoutPlusPrefix(value) {
    if (typeof value !== 'string') return value;
    if (value.indexOf(SECURE_LABEL) === 0) return value;
    if (value.indexOf(SOURCE_LABEL) !== 0) return value;
    return SECURE_LABEL + value.slice(SOURCE_LABEL.length);
  }

  function walkTextNodes(node, visit) {
    if (!node) return;
    if (node.nodeType === 3) {
      visit(node);
      return;
    }
    if (node.nodeType !== 1) return;
    var tag = node.nodeName;
    if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'NOSCRIPT') return;
    var child = node.firstChild;
    while (child) {
      var next = child.nextSibling;
      walkTextNodes(child, visit);
      child = next;
    }
  }

  function relabelTextTree(el) {
    walkTextNodes(el, function (textNode) {
      var next = replaceCheckoutPlusPrefix(textNode.nodeValue);
      if (next !== textNode.nodeValue) textNode.nodeValue = next;
    });
  }

  function relabelAria(el) {
    if (!el || typeof el.getAttribute !== 'function') return;
    var current = el.getAttribute('aria-label');
    if (current == null) return;
    var next = replaceCheckoutPlusPrefix(current);
    if (next !== current) el.setAttribute('aria-label', next);
  }

  function relabelAriaTree(el) {
    relabelAria(el);
    if (!el || typeof el.querySelectorAll !== 'function') return;
    var labeled = el.querySelectorAll('[aria-label]');
    var i;
    for (i = 0; i < labeled.length; i++) relabelAria(labeled[i]);
  }

  function relabelRoot(shadow) {
    var i;
    var j;
    var nodes;
    for (i = 0; i < RELABEL_SELECTORS.length; i++) {
      nodes = shadow.querySelectorAll(RELABEL_SELECTORS[i]);
      for (j = 0; j < nodes.length; j++) {
        relabelTextTree(nodes[j]);
        if (nodes[j].getAttribute('data-target') === 'coverage-button') {
          relabelAriaTree(nodes[j]);
        }
      }
    }
  }

  function injectStyle(shadow) {
    if (typeof shadow.getElementById === 'function' && shadow.getElementById(STYLE_ID)) return;
    if (shadow.querySelector('#' + STYLE_ID)) return;
    var doc = shadow.ownerDocument;
    if (!doc || typeof doc.createElement !== 'function') return;
    var style = doc.createElement('style');
    style.id = STYLE_ID;
    style.textContent = STYLE_TEXT;
    shadow.appendChild(style);
  }

  function applyToRoot(shadow, observer) {
    if (observer) observer.disconnect();
    injectStyle(shadow);
    relabelRoot(shadow);
    if (observer) observer.observe(shadow, OBSERVE_ROOT);
  }

  function trackHost(host) {
    if (liveHosts.indexOf(host) === -1) liveHosts.push(host);
  }

  function unobserveHost(host) {
    if (!hostObservers) return;
    var entry = hostObservers.get(host);
    if (entry && entry.observer) {
      try {
        entry.observer.disconnect();
      } catch (err) {}
    }
    hostObservers.delete(host);
  }

  function pruneHosts() {
    var kept = [];
    var i;
    var host;
    for (i = 0; i < liveHosts.length; i++) {
      host = liveHosts[i];
      if (host && host.isConnected && isInNativeDrawer(host)) {
        kept.push(host);
      } else {
        unobserveHost(host);
      }
    }
    liveHosts = kept;
  }

  function observeHost(host, shadow) {
    var Observer = viewOf(host).MutationObserver;
    var entry = hostObservers && hostObservers.get(host);
    var observer;

    if (entry && entry.root === shadow && entry.observer) {
      applyToRoot(shadow, entry.observer);
      trackHost(host);
      return true;
    }

    if (entry) unobserveHost(host);

    observer = null;
    if (typeof Observer === 'function') {
      observer = new Observer(function () {
        applyToRoot(shadow, observer);
      });
    }

    if (hostObservers) hostObservers.set(host, { observer: observer, root: shadow });
    trackHost(host);
    applyToRoot(shadow, observer);
    return true;
  }

  function enhanceHost(host) {
    if (!isInNativeDrawer(host)) return false;
    var shadow = getToggleRoot(host);
    if (!shadow) return false;
    return observeHost(host, shadow);
  }

  function scan(doc) {
    var drawer;
    var hosts;
    var i;
    pruneHosts();
    drawer = getNativeDrawer(doc);
    if (!drawer || typeof drawer.querySelectorAll !== 'function') return;
    hosts = drawer.querySelectorAll(HOST_SELECTOR);
    for (i = 0; i < hosts.length; i++) enhanceHost(hosts[i]);
  }

  function observeLightDom(doc) {
    var Observer;
    var rootEl;
    var observer;
    if (!doc || (lightObservers && lightObservers.has(doc))) return;
    Observer = viewOf(doc).MutationObserver;
    rootEl = doc.body || doc.documentElement;
    if (!rootEl || typeof Observer !== 'function') return;
    observer = new Observer(function (records) {
      var relevant = records.some(function (record) {
        if (isInNativeDrawer(record.target)) return true;
        var nodes = Array.prototype.slice.call(record.addedNodes).concat(
          Array.prototype.slice.call(record.removedNodes)
        );
        return nodes.some(function (node) {
          return node.nodeType === 1 && (node.matches('#CartDrawer, ' + HOST_SELECTOR) ||
            node.querySelector('#CartDrawer, ' + HOST_SELECTOR));
        });
      });
      if (relevant) scan(doc);
    });
    observer.observe(rootEl, { childList: true, subtree: true });
    if (lightObservers) lightObservers.set(doc, observer);
  }

  function bindEvents(doc) {
    var target;
    var i;
    if (!doc) return;
    if (boundDocs) {
      if (boundDocs.has(doc)) return;
      boundDocs.add(doc);
    }
    target = doc.body || doc;
    if (!target || typeof target.addEventListener !== 'function') return;
    for (i = 0; i < EVENTS.length; i++) {
      target.addEventListener(EVENTS[i], function () {
        scan(doc);
      });
    }
  }

  function start(doc) {
    if (!doc || typeof doc.querySelector !== 'function') return;
    bindEvents(doc);
    observeLightDom(doc);
    scan(doc);
  }

  function init() {
    if (typeof document === 'undefined') return;
    start(document);
  }

  var api = {
    STYLE_TEXT: STYLE_TEXT,
    STYLE_ID: STYLE_ID,
    SOURCE_LABEL: SOURCE_LABEL,
    SECURE_LABEL: SECURE_LABEL,
    HOST_SELECTOR: HOST_SELECTOR,
    HOST_TAG: HOST_TAG,
    EVENTS: EVENTS,
    getToggleRoot: getToggleRoot,
    replaceCheckoutPlusPrefix: replaceCheckoutPlusPrefix,
    isInNativeDrawer: isInNativeDrawer,
    enhanceHost: enhanceHost,
    scan: scan,
    start: start
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }

  root.NativeCartRedo = api;

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  }
})(typeof window !== 'undefined' ? window : globalThis);
